<template>
  <DxPopup
    :visible="visible"
    :show-title="true"
    :title="$t('common.partners')"
    :width="'80%'"
    :height="'85%'"
    :wrapper-attr="{ class: 'partner-selector-popup' }"
    @hiding="onClose"
  >
    <GridPanelHeader
      :grid-ref="$refs.partnerGrid"
      :show-add="true"
      :show-refresh="true"
      :show-filter="false"
      :show-export="false"
      :show-columns="false"
      :no-bottom-margin="true"
      @add="openCreatePartnerDialog"
      @refresh="refreshPartnerGrid"
    />
    <DxDataGrid
      ref="partnerGrid"
      :data-source="partnersDataSurce"
      :show-borders="true"
      :column-auto-width="true"
      :remote-operations="true"
      :hover-state-enabled="true"
      :show-column-headers="true"
      :repaint-changes-only="true"
      :selected-row-keys="selectedPartnerUuids"
      @selection-changed="onGridSelectionChanged($event)"
      @row-click="onRowClick"
      :element-attr="gridAttributes"
    >
      <DxColumn dataField="name" :caption="$t('common.name')" :filter-operations="['contains']" :allowSorting="false" width="25%" />
      <DxColumn
        dataField="legal_form"
        :caption="$t('stock_management.partners.legal_form')"
        :filter-operations="['contains']"
        :allowSorting="false"
        width="15%"
      />
      <DxColumn
        dataField="id_number"
        :caption="$t('stock_management.partners.id_number')"
        :filter-operations="['contains']"
        :allowSorting="false"
        width="15%"
      />
      <DxColumn
        dataField="nr_reg"
        :caption="$t('common.nr_reg')"
        :filter-operations="['contains']"
        :allowSorting="false"
        alignment="center"
        width="15%"
      />
      <DxColumn
        dataField="is_active"
        :caption="$t('common.active')"
        :allowSorting="false"
        width="10%"
        alignment="center"
        :calculateCellValue="getYesNoColumnText"
        :filterType="'list'"
        :lookup="{
          dataSource: [
            { value: 1, text: $t('common.yes') },
            { value: 0, text: $t('common.no') },
          ],
          valueExpr: 'value',
          displayExpr: 'text',
        }"
        cell-template="isActiveViewTemplate"
      />
      <template #isActiveViewTemplate="{ data }">
        <span>
          {{ getYesNoColumnText(data.data.is_active) }}
        </span>
      </template>
      <DxColumn
        dataField="updated_at"
        :caption="$t('common.last_update')"
        :filter-operations="['=']"
        :allowSorting="false"
        dataType="date"
        alignment="center"
        width="15%"
        :format="this.getDxGridDefaultDateFormat"
      />
      <DxSelection :mode="multiselect ? 'multiple' : 'single'" showCheckBoxesMode="always" selectAllMode="page" />
      <DxFilterRow :visible="true" />
      <DxGrouping :autoExpandAll="true" />
      <DxGroupPanel :visible="false" />
      <DxPaging :total="3500" :page-size="15" />
      <DxPager
        :visible="true"
        :allowed-page-sizes="pageSizes"
        display-mode="compact"
        :show-page-size-selector="true"
        :show-info="true"
        :show-navigation-buttons="true"
        :info-text="$t('common.page_info')"
      />
    </DxDataGrid>
    <div style="display: flex; justify-content: flex-end; gap: 12px; margin-top: auto; padding-top: 16px">
      <SmagriaButton :label="$t('common.continue')" customClass="primary col-lg-2 apply-btn" :action="onContinue" />
    </div>
    <CreatePartnerDialog
      :key="createDialogKey"
      v-if="showCreatePartnerDialog"
      :show="showCreatePartnerDialog"
      :data="newPartnerData"
      @close="closeCreatePartnerDialog"
      @onOK="onSaveNewPartner"
    />
  </DxPopup>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import {
  DxDataGrid,
  DxColumn,
  DxSelection,
  DxFilterRow,
  DxGrouping,
  DxGroupPanel,
  DxPaging,
  DxLookup,
  DxPager,
} from "devextreme-vue/data-grid";
import DxPopup from "devextreme-vue/popup";
import CustomStore from "devextreme/data/custom_store";
import SmagriaButton from "@/components/Common/SmagriaButton.vue";
import CreatePartnerDialog from "~/pages/stock-management/sm-partners/partials/CreatePartnerDialog.vue";
import GridPanelHeader from "@/components/DxGrid/GridPanelHeader.vue";
import { errorMessage } from "~/helpers/common";

export default {
  name: "PartnerSelector",
  components: {
    DxDataGrid,
    DxColumn,
    DxPopup,
    DxSelection,
    DxFilterRow,
    DxGrouping,
    DxGroupPanel,
    DxPaging,
    DxLookup,
    DxPager,
    SmagriaButton,
    CreatePartnerDialog,
    GridPanelHeader,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    multiselect: {
      type: Boolean,
      default: () => true,
    },
    selectedPartnerUuids: {
      type: Array,
      default: () => [],
    },
    filterData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      gridAttributes: {
        class: "smagria-grid partner_selector_grid",
      },
      partnersDataSurce: new CustomStore({
        key: "uuid",
        load: async (loadOptions) => {
          let dataSource = {
            data: [],
            totalCount: 0,
          };

          let pageLimit = loadOptions.take;
          let pageNumber = (loadOptions.skip + loadOptions.take) / pageLimit;

          let payload = {
            page: pageNumber,
            limit: pageLimit,
            filter: loadOptions.filter,
            sort: loadOptions.sort,
          };

          if (this.filterData && Object.keys(this.filterData).length > 0) {
            Object.assign(payload, this.filterData);
          }

          try {
            await this.getPartners(payload).then((response) => {
              dataSource.data = response.data;
              dataSource.totalCount = response?.pagination?.total ?? 1;
            });

            return dataSource;
          } catch (error) {
            console.error("Error fetching partners:", error);
            throw error;
          }
        },
        remove: this.removeWeighingOnScale,
      }),
      gridRemoteOperations: {
        paging: true,
        filtering: true,
        sorting: true,
      },
      pageSizes: [15, 25, 100],
      localSelectedPartnerUuids: [...this.selectedPartnerUuids],
      showCreatePartnerDialog: false,
      createDialogKey: 0,
      newPartnerData: {
        uuid: null,
        name: null,
        id_number: null,
        nr_reg: null,
        partner_type: null,
        legal_form: null,
        is_active: 1,
        bank_data: [],
        contact_person_data: [],
        county: null,
      },
    };
  },
  computed: {
    ...mapGetters({
      getDxGridDefaultDateFormat: "settings/getDxGridDefaultDateFormat",
    }),
  },
  watch: {
    selectedPartnerUuids: {
      deep: true,
      handler(newData) {
        this.localSelectedPartnerUuids = [...newData];
        this.$refs.partnerGrid.instance.selectRows(newData, false);
      },
    },
  },
  methods: {
    ...mapActions({
      getPartners: "smPartners/getSmPartners",
      createPartner: "smPartners/createSmPartner",
    }),
    onClose() {
      this.$emit("close");
    },
    refreshPartnerGrid() {
      return this.$refs.partnerGrid?.instance?.refresh();
    },
    openCreatePartnerDialog() {
      this.newPartnerData = {
        uuid: null,
        name: null,
        id_number: null,
        nr_reg: null,
        partner_type: null,
        legal_form: null,
        is_active: 1,
        bank_data: [],
        contact_person_data: [],
        county: null,
      };
      this.createDialogKey += 1;
      this.showCreatePartnerDialog = true;
    },
    closeCreatePartnerDialog() {
      this.showCreatePartnerDialog = false;
    },
    async onSaveNewPartner(partner) {
      try {
        const createdPartner = await this.createPartner(partner);
        this.closeCreatePartnerDialog();
        await this.refreshPartnerGrid();
        const created = createdPartner?.data || createdPartner;
        if (created?.uuid) {
          this.$refs.partnerGrid?.instance?.selectRows([created.uuid], false);
        }
      } catch (error) {
        errorMessage(this, this.$t("error_alert_text"), error);
      }
    },
    onContinue() {
      const selectedRows = this.$refs.partnerGrid.instance.getSelectedRowsData();
      this.$emit("continue", selectedRows ?? []); // Emit the selected partners to the parent component

      this.$refs.partnerGrid.instance.clearSelection();
      this.localSelectedPartnerUuids = [];
      this.$emit("update:selectedPartnerUuids", []);
    },
    onGridSelectionChanged(e) {},
    onRowClick(e) {
      // Prevent default behavior if the row is already selected and multiselect is false
      if (!this.multiselect) {
        return;
      }

      const rowKey = e.key; // Get the unique key of the clicked row
      const selectedIndex = this.selectedPartnerUuids.indexOf(rowKey);

      if (selectedIndex === -1) {
        // If the row is not selected, add it to the selection
        this.selectedPartnerUuids.push(rowKey);
      } else {
        // If the row is already selected, remove it from the selection
        this.selectedPartnerUuids.splice(selectedIndex, 1);
      }

      // Update the grid's selected rows
      this.$refs.partnerGrid.instance.selectRows(this.selectedPartnerUuids, false);
    },
    getYesNoColumnText(value) {
      return value === 1 ? this.$t("common.yes") : this.$t("common.no");
    },
  },
};
</script>

<style lang="scss">
.partner-selector-popup .dx-popup-content {
  padding-top: 0 !important;
}
</style>

<style lang="scss" scoped>
.partner_selector_grid {
  height: calc(95vh - 290px) !important;
}
</style>
